import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { Box } from '../Box';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp';

export type DataCardsProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Should be at least one `DataCard` component
   */
  children: ReactNode;
  /**
   * String to pass to the `max-width` property of its children. [View the syntax on MDN →](https://developer.mozilla.org/en-US/docs/Web/CSS/max-width#syntax)
   */
  cardMaxWidth?: ResponsiveObject<string> | string;
  /**
   * Sets the color scheme of the cards, can be overriden at the card level
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Desired number of columns to display.
   */
  columns?: ResponsiveObject<number | 'auto'> | number | 'auto';
};

type StyledDataCardsTypes = {
  $cardMaxWidth: string;
  $colorScheme: ColorSchemeTypes;
  $columns: number | 'auto';
};

const StyledDataCards = styled(Box)<StyledDataCardsTypes>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  --wui-data-cards-column-count: ${({ $columns }) => ($columns === 'auto' ? undefined : $columns)};
  --wui-data-cards-total-column-gap-width: calc(var(--wui-data-cards-column-count) - 1) *
    var(--wui-space-02);
  --wui-data-cards-column-width: calc(
    (100% - var(--wui-data-cards-total-column-gap-width)) / var(--wui-data-cards-column-count)
  );

  > * {
    min-width: 120px;
    flex-basis: var(--wui-data-cards-column-width, 0);
    max-width: ${({ $cardMaxWidth }) => $cardMaxWidth};
  }
`;

/**
 * Displays a series of cards tailor made for displaying analytics data
 */
export const DataCards = ({
  children,
  cardMaxWidth = 'none',
  colorScheme = 'inherit',
  columns = 'auto',
  ...props
}: DataCardsProps): JSX.Element => {
  const responsiveCardMaxWidth = useResponsiveProp(cardMaxWidth);
  const responsiveColumns = useResponsiveProp(columns);

  return (
    <StyledDataCards
      {...props}
      $cardMaxWidth={responsiveCardMaxWidth}
      $colorScheme={colorScheme}
      $columns={responsiveColumns}
      alignItems="stretch"
      direction="row"
      fill="horizontal"
      gap="space-02"
      wrapItems={true}
    >
      {children}
    </StyledDataCards>
  );
};

DataCards.displayName = 'DataCards';
